import { useMemo } from 'react'
import { Link } from 'react-router-dom'

import BudgetPacingProgressBar from '~/components/BudgetPacingProgressBar'
import ProgressBar from '~/components/ProgressBar'
import Tooltip from '~/components/Tooltip'
import { InputMoney } from '~/components/unform'
import { formatMoney } from '~/helpers'
import { useAPPTranslation } from '~/i18n/useAPPTranslation'

import * as S from './styles'

type ProgressSectionProps = {
  data: DailyBudgetProps['data']
  progress: React.ReactNode
  t: (key: string) => string
  isEditMode?: boolean
}

/**
 * Barra primeiro, par usado/disponível depois — a ordem do desenho
 * (`.review-invest-bar` acima de `.review-invest-meta`).
 */
const ProgressSection = ({
  data,
  progress,
  t,
  isEditMode = false
}: ProgressSectionProps) => (
  <S.ContainerProgress $fixedWidth={isEditMode}>
    {progress}
    <header>
      <div className="column">
        <h6 className="title">{t('rm:used')}</h6>
        <span className="value">{data.budget.spent}</span>
      </div>
      <div className="column">
        <h6 className="title">{t('rm:available')}</h6>
        <span className="value">
          {formatMoney(
            data.budget.raw.dailyBudget - data.budget.raw.dailyConsumed
          )}
        </span>
      </div>
    </header>
  </S.ContainerProgress>
)

export const DailyBudget = ({
  data,
  inputName,
  isEditMode = false,
  isLoading = false,
  budgetPacingEnabled = false,
  spendReportsTo,
  onDailyBudgetChange
}: DailyBudgetProps) => {
  const { t } = useAPPTranslation()

  const pacing = data.raw?.daily_pacing ?? 'on_track'

  const progress = useMemo(
    () =>
      budgetPacingEnabled && !isEditMode ? (
        <BudgetPacingProgressBar
          variant="compact"
          pacing={pacing}
          goal={data.budget.raw.dailyBudget}
          value={data.budget.raw.dailyConsumed}
        />
      ) : (
        <ProgressBar
          goal={data.budget.raw.dailyBudget}
          value={data.budget.raw.dailyConsumed}
        />
      ),
    [budgetPacingEnabled, isEditMode, pacing, data.budget.raw]
  )

  const progressSection = (
    <ProgressSection
      data={data}
      progress={progress}
      t={t}
      isEditMode={isEditMode}
    />
  )

  return (
    /* Em leitura a coluna empilha valor, barra e consumo, como o desenho
       mostra; na edição o input continua ao lado da barra, que é onde o campo
       cabe sem empurrar o formulário. */
    <S.Wrapper
      className={isEditMode ? 'd-flex align-items-start h-100' : undefined}
    >
      <div className="mainColumn">
        <S.Text>
          {t('rm:dailyBudget')}
          <Tooltip text={t('rm:dailyBudget.tooltip')} />
        </S.Text>

        {isEditMode ? (
          <InputMoney
            id={inputName}
            name={inputName}
            loading={isLoading}
            required
            onChange={onDailyBudgetChange}
          />
        ) : (
          <p>{formatMoney(data.settings.daily_budget)}</p>
        )}
      </div>

      <div className={isEditMode ? 'align-self-end' : undefined}>
        {spendReportsTo && !isEditMode ? (
          <Link
            to={spendReportsTo}
            className="text-decoration-none"
            style={{ color: 'inherit' }}
          >
            {progressSection}
          </Link>
        ) : (
          progressSection
        )}
      </div>
    </S.Wrapper>
  )
}
